import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useToast } from '@/hooks/use-toast';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import OrderSummary from '@/components/business/reseller/order-summary';
import OrderSuccessView from '@/components/business/reseller/OrderSuccessView';
import { orderFormSchema, OrderFormValues } from '@/components/business/reseller/orderFormSchema';
import { useResellerPlans } from '@/hooks/license/useResellerPlans';
import OrderFormContainer from '@/components/business/reseller/order/OrderFormContainer';
import OrderPageHeader from '@/components/business/reseller/order/OrderPageHeader';
import LoadingState from '@/components/business/reseller/order/LoadingState';
import { useStripePayment } from '@/hooks/stripe/useStripePayment';
import { createResellerOrder } from '@/utils/resellerOrderService';
import { generateOrderNumber } from '@/utils/orderNumberGenerator';

const BusinessResellerOrder = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isPaymentComplete, setIsPaymentComplete] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('USA');
  const { isProcessing: isProcessingPayment, processPayment } = useStripePayment();
  const [orderNumber, setOrderNumber] = useState<string>('');
  
  const { resellerPlan, isLoadingPlans } = useResellerPlans(true);
  
  const [promoCode, setPromoCode] = useState(sessionStorage.getItem('promoCode') || '');
  const [promoDiscount, setPromoDiscount] = useState(
    sessionStorage.getItem('promoDiscount') ? 
    parseFloat(sessionStorage.getItem('promoDiscount') || '0') : 0
  );
  
  useEffect(() => {
    setOrderNumber(generateOrderNumber('RO'));
  }, []);
  
  const form = useForm<OrderFormValues>({
    resolver: zodResolver(orderFormSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      companyName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'USA',
      taxId: '',
      taxIdType: 'EIN',
      desiredSubdomain: '',
      agreeTerms: false as unknown as true,
    }
  });

  const watchCountry = form.watch('country');
  
  useEffect(() => {
    if (watchCountry) {
      setSelectedCountry(watchCountry);
      
      if (watchCountry === 'USA') {
        form.setValue('taxIdType', 'EIN');
      } else {
        form.setValue('taxIdType', 'VAT');
      }
    }
  }, [watchCountry, form]);

  const onSubmit = async (data: OrderFormValues) => {
    setIsLoading(true);
    
    try {
      console.log('Form data submitted:', data);
      console.log('Promo code applied:', promoCode);
      console.log('Promo discount:', promoDiscount);
      console.log('Selected plan:', resellerPlan);
      console.log('Order number:', orderNumber);
      
      const domain = data.desiredSubdomain ? `${data.desiredSubdomain}.referme.site` : '';
      
      const newOrder = await createResellerOrder({
        businessName: data.companyName,
        businessEmail: data.email,
        contactName: `${data.firstName} ${data.lastName}`,
        contactEmail: data.email,
        contactPhone: data.phone,
        plan: 'pro', // Default to pro plan
        domain: domain,
        paymentAmount: (resellerPlan?.setupFee || 500) - promoDiscount,
        paymentMethod: 'credit_card',
        businessAddress: {
          street: data.address,
          city: data.city,
          state: data.state,
          zip: data.zipCode,
          country: data.country,
        },
      });
      
      console.log('New order created:', newOrder);
      
      const amount = resellerPlan?.setupFee || 500;
      const discountedAmount = amount - promoDiscount;
      const annualPrice = resellerPlan?.annualPrice || 999;
      
      const paymentMethodId = 'pm_card_visa';
      
      const result = await processPayment(paymentMethodId, {
        amount: discountedAmount * 100,
        description: `Setup fee for ${resellerPlan?.name || 'Reseller Plan'} (Order: ${newOrder.orderNumber})`,
        currency: 'usd',
        planId: resellerPlan?.id,
        planType: 'reseller',
        metadata: {
          orderNumber: newOrder.orderNumber,
          customerName: `${data.firstName} ${data.lastName}`,
          companyName: data.companyName,
          email: data.email,
          promoCode: promoCode || undefined,
          promoDiscount: promoDiscount > 0 ? promoDiscount.toString() : undefined,
          annualPrice: annualPrice.toString()
        }
      });
      
      if (!result.success) {
        throw new Error('Payment processing failed');
      }
      
      setIsPaymentComplete(true);
      setOrderNumber(newOrder.orderNumber);
      
      toast({
        title: "Order processed successfully",
        description: `Your order #${newOrder.orderNumber} has been processed. You will receive an email confirmation shortly.`
      });
      
      sessionStorage.removeItem('promoCode');
      sessionStorage.removeItem('promoDiscount');
      
    } catch (error) {
      console.error('Error processing order:', error);
      toast({
        title: "Error processing order",
        description: "There was a problem processing your order. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isPaymentComplete) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header isLoggedIn={false} />
        <main className="flex-grow flex items-center justify-center p-4">
          <OrderSuccessView 
            planDetails={resellerPlan} 
            orderDetails={{
              orderNumber: orderNumber,
              setupFee: resellerPlan?.setupFee || 500,
              discount: promoDiscount,
              totalAmount: (resellerPlan?.setupFee || 500) - promoDiscount,
              promoCode: promoCode,
              annualPrice: resellerPlan?.annualPrice || 999
            }}
          />
        </main>
        <Footer />
      </div>
    );
  }

  if (isLoadingPlans) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header isLoggedIn={false} />
        <main className="flex-grow flex items-center justify-center">
          <LoadingState />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header isLoggedIn={false} />
      
      <main className="flex-grow py-10 px-4">
        <div className="max-w-4xl mx-auto">
          <OrderPageHeader planName={resellerPlan?.name} orderNumber={orderNumber} />
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2">
              <OrderFormContainer 
                form={form} 
                isLoading={isLoading || isProcessingPayment} 
                onSubmit={onSubmit}
                selectedCountry={selectedCountry}
              />
            </div>
            
            <div>
              <OrderSummary 
                planDetails={resellerPlan} 
                promoCode={promoCode}
                promoDiscount={promoDiscount}
                setPromoCode={setPromoCode}
                setPromoDiscount={setPromoDiscount}
              />
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default BusinessResellerOrder;
